import { getSubtypeValuesForType } from '@resistapp/common/sample-sheet';
import {
  friendlySampleSubTypeOrType,
  SampleSubType,
  SampleType,
} from '@resistapp/common/sampling-point-types';
import { SamplingPoint } from '@resistapp/common/types';
import { chain, isEqual, sortBy, uniq, values } from 'lodash';

type SamplingPointTypeAndSubtype = Pick<SamplingPoint, 'id' | 'type' | 'subtype'>;

export interface SampleSubtypeBucket {
  parentType: SampleType;
  subtype: SampleSubType;
  samplingPointIds: number[];
}

export function isParentSampleType(value: string): value is SampleType {
  return values(SampleType as Record<string, string>).includes(value);
}

export function getFriendlySampleSubtypeOrTypeLabel(
  type: SampleType | null | undefined,
  subtype: SampleSubType | null | undefined,
): string | null {
  if (subtype) {
    return friendlySampleSubTypeOrType(subtype);
  }

  if (type) {
    return friendlySampleSubTypeOrType(type);
  }

  return null;
}

export function getPresentSubtypeBucketsForType(
  samplingPoints: SamplingPointTypeAndSubtype[],
  sampleType: SampleType,
): SampleSubtypeBucket[] {
  const samplingPointIdsBySubtype = chain(samplingPoints)
    .filter(samplingPoint => samplingPoint.type === sampleType && samplingPoint.subtype != null)
    .groupBy(samplingPoint => samplingPoint.subtype)
    .mapValues(points => sortBy(points.map(point => point.id)))
    .value();

  return getSubtypeValuesForType(sampleType)
    .filter(subtype => (samplingPointIdsBySubtype[subtype] ?? []).length > 0)
    .map(subtype => ({
      parentType: sampleType,
      subtype,
      samplingPointIds: samplingPointIdsBySubtype[subtype] ?? [],
    }));
}

export function getExactSelectedSubtypeBucket(
  selectedSampleTypeGroup: string,
  selectedSamplingPointIdsOrdered: number[],
  samplingPoints: SamplingPointTypeAndSubtype[],
): SampleSubtypeBucket | undefined {
  if (!selectedSamplingPointIdsOrdered.length || !isParentSampleType(selectedSampleTypeGroup)) {
    return undefined;
  }

  const normalizedSelectedSamplingPointIds = sortBy(uniq(selectedSamplingPointIdsOrdered));

  return getPresentSubtypeBucketsForType(samplingPoints, selectedSampleTypeGroup).find(bucket =>
    isEqual(bucket.samplingPointIds, normalizedSelectedSamplingPointIds),
  );
}
